// Per-action field resolution + per-dealer update for Bulk Update Dealers.
//
// Each action's current/new-value logic mirrors the same field the dealer
// detail page already exposes (DealerAttributesCard, ServicesCard /
// service_types, CookieConsentsCard, SiteStatusCard) — see
// docs/specs/bulk-update-dealers-20260904.md §4.
//
// `apply` is wired for real for `ddmMigration`, `certification`, `service`,
// and `siteStatus`; only `cookieConsent` is still mocked — it never calls
// the backend and never persists anything. The next phase replaces its body
// with a call to the same endpoint the dealer-detail-page card uses (noted
// below) instead of writing new logic — see §4/§9 of the spec.

import client, { formatApiError } from "@/lib/api";
import { ADDABLE_SERVICE_TYPES } from "@/lib/serviceTypes";

export const CERTIFICATION_FIELDS = ["certified_seller", "financial_certified", "skoda_club"];
export const SERVICE_TYPES = ADDABLE_SERVICE_TYPES;
export const SITE_STATUS_ACTION_OPTIONS = ["deploy", "bring_online", "take_offline", "disable_site", "delete_site"];

// Mirrors backend CMS_SITE_STATUS_ACTIONS / SiteStatusCard.jsx ACTIONS_BY_STATUS
const SITE_STATUS_VALID_ACTIONS = {
  no_site: ["deploy"],
  offline: ["bring_online", "disable_site", "delete_site"],
  online: ["take_offline", "disable_site"],
  disabled: ["bring_online", "take_offline", "delete_site"],
  site_deleted: ["deploy"],
};

// Resulting cms_site_status after each action, for dealers where the action is valid.
const SITE_STATUS_RESULT = {
  deploy: "offline",
  bring_online: "online",
  take_offline: "offline",
  disable_site: "disabled",
  delete_site: "site_deleted",
};

function mockDelay() {
  return new Promise((resolve) => setTimeout(resolve, 150 + Math.random() * 250));
}

// TEMP DISABLE — OneTrust tenant is LIVE; bulk-queueing many dealers at once
// is too risky while this is still being finished. Flip back to true (and
// remove this comment) to re-enable; see the matching guard in
// ACTIONS.cookieConsent.apply below and BulkUpdateDealers.jsx's runUpdate.
export const COOKIE_CONSENT_BULK_ENABLED = false;

export const ACTIONS = {
  ddmMigration: {
    id: "ddmMigration",
    fieldLabelKey: () => "dealership.managementMigratedToDdm",
    resolve(dealer, params) {
      const current = !!dealer.management_migrated_to_ddm;
      return { current, next: params.setTo, na: false };
    },
    formatValue(t, value) {
      return t(value ? "common.yes" : "common.no");
    },
    // Real: same call as DealersList.jsx (toggleDdmMigrated) →
    // PATCH /dealers/{id}/ddm-migration. That endpoint already writes its
    // own per-dealer audit entry (dealer.management_migrated_to_ddm.update),
    // so per-dealer audit logging comes for free from this reuse — see spec §7.2.
    async apply(dealer, params) {
      const before = { management_migrated_to_ddm: !!dealer.management_migrated_to_ddm };
      await client.patch(`/dealers/${dealer.id}/ddm-migration`, { migrated: params.setTo });
      return { before, after: { management_migrated_to_ddm: params.setTo } };
    },
  },

  certification: {
    id: "certification",
    fieldLabelKey: (params) => `bulkUpdate.fields.${params.field}`,
    resolve(dealer, params) {
      const current = !!dealer.certifications?.[params.field];
      return { current, next: params.setTo, na: false };
    },
    formatValue(t, value) {
      return t(value ? "common.yes" : "common.no");
    },
    // Real: same call as DealerAttributesCard.jsx → PATCH
    // /dealers/{id}/certifications. That endpoint does a per-field $set
    // (exclude_none), so sending just the one changed field is enough — no
    // need to round-trip the whole certifications sub-object like the card
    // does. Already writes its own per-dealer audit entry
    // (dealer.certifications.update), so audit logging comes free here too.
    async apply(dealer, params) {
      const before = { [params.field]: !!dealer.certifications?.[params.field] };
      await client.patch(`/dealers/${dealer.id}/certifications`, { [params.field]: params.setTo });
      return { before, after: { [params.field]: params.setTo } };
    },
  },

  service: {
    id: "service",
    fieldLabelKey: (params) => `serviceTypes.${params.serviceType}`,
    resolve(dealer, params) {
      const has = (dealer.service_types || []).includes(params.serviceType);
      return { current: has, next: params.mode === "add", na: false };
    },
    formatValue(t, value) {
      return t(value ? "common.yes" : "common.no");
    },
    // Real: same shared function as ServicesCard.jsx's Add/Remove controls →
    // POST/DELETE /dealers/{id}/services(/{type}). Newly-added services
    // inherit contacts/opening hours per SERVICE_INHERITANCE (backend
    // models.py) — see spec §4/§9.
    async apply(dealer, params) {
      const has = (dealer.service_types || []).includes(params.serviceType);
      if (params.mode === "add") {
        await client.post(`/dealers/${dealer.id}/services`, { type: params.serviceType });
      } else {
        await client.delete(`/dealers/${dealer.id}/services/${params.serviceType}`);
      }
      return { before: { has_service: has }, after: { has_service: params.mode === "add" } };
    },
  },

  cookieConsent: {
    id: "cookieConsent",
    fieldLabelKey: () => "bulkUpdate.fields.cookieConsentStatus",
    resolve(dealer, params) {
      const current = dealer.cookie_consents_local_status || null;
      if (params.direction === "add") {
        if (!dealer.management_migrated_to_ddm) {
          return { current, next: null, na: true, naReasonKey: "dealership.cookieConsentsLocal.mustBeMigratedHint" };
        }
        return { current, next: "SetupOnNextRun", na: false };
      }
      // remove
      return { current, next: null, na: false };
    },
    formatValue(t, value) {
      if (!value) return t("bulkUpdate.review.noValue");
      return t(`dealership.cookieConsentsLocal.status.${value}`);
    },
    // Real implementation (next phase), "add": same call as
    // CookieConsentsCard.jsx → PATCH /dealers/{id}/cookie-consents/setup.
    // "remove": no existing endpoint anywhere — new shared function (spec §4/§9).
    async apply(dealer, params) {
      if (!COOKIE_CONSENT_BULK_ENABLED) {
        throw new Error("Bulk update queueing is temporarily disabled at code-level");
      }
      // --- mock body, commented out while disabled above ---
      // await mockDelay();
      // return {
      //   before: { cookie_consents_local_status: dealer.cookie_consents_local_status || null },
      //   after: { cookie_consents_local_status: params.direction === "add" ? "SetupOnNextRun" : null },
      // };
    },
  },

  siteStatus: {
    id: "siteStatus",
    fieldLabelKey: () => "dealership.siteStatus.title",
    resolve(dealer, params) {
      const current = dealer.cms_site_status || "no_site";
      // Mirrors backend dealer_site_status_action's own precondition
      // (server.py) — a dealer with a change already awaiting CMS sync
      // rejects *any* further action with a 409, regardless of which one.
      if (dealer.cms_status_change_pending) {
        return { current, next: null, na: true, naReasonKey: "bulkUpdate.review.pendingChange" };
      }
      const valid = (SITE_STATUS_VALID_ACTIONS[current] || []).includes(params.action);
      if (!valid) {
        return { current, next: null, na: true, naReasonKey: "bulkUpdate.review.notApplicable" };
      }
      return { current, next: SITE_STATUS_RESULT[params.action], na: false };
    },
    formatValue(t, value) {
      return t(`dealership.siteStatus.${value}`);
    },
    // Real: same call as SiteStatusCard.jsx (runAction) →
    // POST /dealers/{id}/site-status/action. That endpoint doesn't flip
    // cms_site_status immediately — it sets cms_status_change_pending +
    // cms_site_status_pending and waits for the CMS to confirm — so `after`
    // reflects that real pending state, not an instant transition.
    // "delete_site" requires confirm_text to match the dealer's website_url
    // server-side; the bulk flow supplies it automatically from the same
    // dealer data already loaded (no per-row retyping) since the multi-step
    // select → review → update flow already is the "are you sure".
    async apply(dealer, params) {
      const current = dealer.cms_site_status || "no_site";
      const extra = params.action === "delete_site"
        ? { confirm_text: dealer.website_url || "" }
        : {};
      await client.post(`/dealers/${dealer.id}/site-status/action`, { action: params.action, ...extra });
      return {
        before: { cms_site_status: current },
        after: { cms_site_status_pending: SITE_STATUS_RESULT[params.action], cms_status_change_pending: true },
      };
    },
  },
};

// Only importer in this app (BID=107, see dealer_json_import.py) — bare
// dealer numbers (no "{BID}-" prefix) are normalized to it so the textarea
// accepts codes copied with or without the prefix.
const BID_PREFIX = "107-";

export function parseDealerCodes(text) {
  return (text || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((code) => (/^\d+$/.test(code) ? `${BID_PREFIX}${code}` : code));
}

/** Builds one review row per pasted code. Dealer lookup misses and repeated
 * codes are both flagged as issues (§3.2 of the spec) rather than dropped,
 * so the user can see exactly what was skipped and why. */
export function buildReviewRows(codes, dealersByCode, action, params) {
  const seen = new Set();
  return codes.map((code, idx) => {
    const duplicate = seen.has(code);
    seen.add(code);
    const dealer = dealersByCode.get(code) || null;
    const rowId = `${code}__${idx}`;

    if (!dealer) {
      return { rowId, code, dealer: null, notFound: true, duplicate: false, na: false };
    }
    const { current, next, na, naReasonKey } = ACTIONS[action].resolve(dealer, params);
    return { rowId, code, dealer, notFound: false, duplicate, na, naReasonKey, current, next };
  });
}

/** Shared per-dealer runner all four actions plug into — orchestration
 * (progress + result aggregation) lives here once, not per action. */
export async function runBulkUpdate(action, params, rows, { onProgress } = {}) {
  const results = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    try {
      const { before, after } = await ACTIONS[action].apply(row.dealer, params);
      results.push({ ...row, success: true, before, after });
    } catch (e) {
      results.push({ ...row, success: false, error: formatApiError(e?.response?.data?.detail) || "Update failed" });
    }
    if (onProgress) onProgress(i + 1, rows.length);
  }
  return results;
}
